'use client';

import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import useUser from '@alltrails/context/hooks/useUser';
import { useFormatMessage } from '@alltrails/core/hooks/useFormatMessage';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import { betweenHours } from '@alltrails/data-formatting/dateTimeConverters';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { SPACE_16 } from '@alltrails/denali/tokens';
import type { User } from '@alltrails/types/User';
import { setGreetings } from '../../slices/greetings';
import type { HeroData } from '../../utils/getHeroImages';
import { useAppDispatch } from '../../utils/redux';
import HomeSearchBar from '../HomeSearchBar';
import RotatingHero from '../RotatingHero';
import styles from './styles.module.scss';

export type Greetings = { daily_start_time: string; daily_end_time: string; custom_title: string[] }[];

const messages = defineMessages({
  FIND_YOUR_OUTDOORS: {
    defaultMessage: 'Find your outside'
  }
});

const FIRST_NAME_PLACEHOLDER = '{first_name}';

const chooseGreeting = (user: User | null, greetingIndex: number, defaultText: string, greetings?: Greetings): string => {
  if (!(user && greetings)) {
    return defaultText;
  }

  let text = '';
  greetings.forEach(greet => {
    if (text.length === 0) {
      if (greet.daily_start_time && greet.daily_end_time && betweenHours(greet.daily_start_time, greet.daily_end_time)) {
        const customTitle = greet.custom_title[greetingIndex] ?? greet.custom_title[0];
        if (customTitle) {
          text = customTitle;
        }
      }
    }
  });

  if (text.length === 0) {
    text = defaultText;
  }

  if (user && user.firstName && text.includes(FIRST_NAME_PLACEHOLDER)) {
    text = text.replace(FIRST_NAME_PLACEHOLDER, user.firstName);
  }

  return text;
};

type Props = {
  greetings?: Greetings;
  heroData: HeroData;
};

// https://www.figma.com/file/o24ZQP00Um7Ie23xWMwIoL/Sketches?type=design&node-id=113-34181&mode=dev
// Desktop (logged out): https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=593%3A31304&t=42V2GrDcXYKs9Nf2-1
// Desktop (logged in): https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=593%3A31686&t=42V2GrDcXYKs9Nf2-1
// Mobile (logged out): https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=593%3A31994&t=42V2GrDcXYKs9Nf2-1
// Mobile (logged in): https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=593%3A31825&t=42V2GrDcXYKs9Nf2-1
const HomeHeader = ({ greetings: initialGreetings, heroData }: Props) => {
  const languageRegionCode = useLanguageRegionCode();
  const user = useUser();
  const dispatch = useAppDispatch();
  const {
    formattedDefaultMessages: { FIND_YOUR_OUTDOORS }
  } = useFormatMessage(messages);
  const isMobile = useIsMobileSizedScreen();

  const greetings = useSelector((state: { greetings?: { greetings?: Greetings } }) => state.greetings?.greetings);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    if (initialGreetings && !greetings) {
      dispatch(setGreetings(initialGreetings));
    }
  }, [initialGreetings, greetings, dispatch]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const headerText = useMemo(
    () => chooseGreeting(user, heroData.greetingIndex, FIND_YOUR_OUTDOORS, greetings),
    [user, heroData.greetingIndex, FIND_YOUR_OUTDOORS, greetings]
  );

  if (user) {
    const buttonContainerStyle = isMobile ? { marginBottom: SPACE_16 } : undefined;

    return (
      <RotatingHero images={heroData.images} className={classNames(styles.loggedInContainer, { [styles.authenticated]: user })} buttonContainerStyle={buttonContainerStyle}>
        <div className={styles.gradient} />
        <h1 className={styles.header}>{isMounted ? headerText : ''}</h1>
        <div className={classNames(styles.searchBarContainer, styles.loggedInSearchContainer)}>
          <HomeSearchBar />
        </div>
      </RotatingHero>
    );
  }

  return (
    <RotatingHero images={heroData.images} className={styles.container}>
      <div className={styles.gradient} />
      <div className={styles.contentContainer}>
        <h1 className={classNames(styles.header, styles.loggedOutHeader)}>{headerText}</h1>
        <div className={styles.searchBarContainer}>
          <HomeSearchBar />
        </div>
        <div className={styles.exploreLink}>
          <a href={wrapUrlSafe('/explore', languageRegionCode)}>
            <FormattedMessage defaultMessage="Explore nearby trails" />
          </a>
        </div>
      </div>
    </RotatingHero>
  );
};

export default HomeHeader;
